import {
  VESSEL_CARD_BALTIC_COMPARISON_MODES,
  VESSEL_CARD_BALTIC_CONSUMPTION_EXPONENT,
} from "../constants/vessel-card-baltic.constants";
import type {
  VesselCardBalticBenchmark,
  VesselCardBalticRow,
  VesselCardIndicatorTone,
  VesselCardPerformanceRow,
} from "../models/vessel-card.models";

// Matches vessel and benchmark conditions by name even when their ordering differs.
// Retains the four requested rows when expected vessel performance is unavailable.
export const toVesselCardBalticRows = (
  performance: VesselCardPerformanceRow[],
  benchmark: VesselCardBalticBenchmark | null | undefined,
): VesselCardBalticRow[] =>
  VESSEL_CARD_BALTIC_COMPARISON_MODES.map((mode) => {
    const matchingMode = toBalticModeKey(mode);
    const expected = performance.find((row) => toBalticModeKey(row.mode) === matchingMode);
    const index = benchmark?.rows.find((row) => toBalticModeKey(row.mode) === matchingMode);
    const expectedSpeed = toAvailableBalticMetric(expected?.expectedSpeed);
    const expectedConsumption = toAvailableBalticMetric(expected?.expectedConsumption);
    const indexSpeed = toAvailableBalticMetric(index?.speed);
    const indexConsumption = toAvailableBalticMetric(index?.consumption);
    const expectedConsumptionAtIndexSpeed = calculateConsumptionAtBalticSpeed(
      expectedSpeed,
      expectedConsumption,
      indexSpeed,
    );
    const comparisonPercent = calculateBalticComparisonPercent(indexConsumption, expectedConsumptionAtIndexSpeed);

    return {
      mode,
      expectedSpeed,
      expectedConsumption,
      indexSpeed,
      indexConsumption,
      expectedAtIndexSpeed: indexSpeed,
      expectedConsumptionAtIndexSpeed,
      comparisonPercent,
      indicatorTone: toBalticComparisonTone(comparisonPercent),
    };
  });

// Rounds only displayed values; calculations continue to use the original precision.
export const formatVesselCardMetric = (value: number | null): string =>
  value !== null && Number.isFinite(value) ? value.toFixed(1) : "—";

export const formatVesselCardBalticPercent = (value: number | null): string =>
  value !== null && Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";

// === BALTIC COMPARISON ===

const toBalticModeKey = (mode: string): string =>
  mode
    .trim()
    .replace(/[\s_-]+/g, " ")
    .toLowerCase();

const toAvailableBalticMetric = (value: number | null | undefined): number | null =>
  value !== null && value !== undefined && Number.isFinite(value) && value >= 0 ? value : null;

const calculateConsumptionAtBalticSpeed = (
  expectedSpeed: number | null,
  expectedConsumption: number | null,
  indexSpeed: number | null,
): number | null => {
  if (
    expectedSpeed === null ||
    expectedSpeed <= 0 ||
    expectedConsumption === null ||
    indexSpeed === null ||
    indexSpeed <= 0
  ) {
    return null;
  }

  const consumption =
    expectedConsumption * Math.pow(indexSpeed / expectedSpeed, VESSEL_CARD_BALTIC_CONSUMPTION_EXPONENT);

  return Number.isFinite(consumption) ? consumption : null;
};

const calculateBalticComparisonPercent = (
  indexConsumption: number | null,
  expectedConsumptionAtIndexSpeed: number | null,
): number | null => {
  if (indexConsumption === null || expectedConsumptionAtIndexSpeed === null || expectedConsumptionAtIndexSpeed <= 0) {
    return null;
  }

  const comparisonPercent = (indexConsumption / expectedConsumptionAtIndexSpeed) * 100;

  return Number.isFinite(comparisonPercent) ? comparisonPercent : null;
};

// Above 100% means the vessel consumes less than the benchmark at the same speed.
// An equal or unavailable comparison has no better-or-worse rating.
const toBalticComparisonTone = (comparisonPercent: number | null): VesselCardIndicatorTone => {
  if (comparisonPercent === null || comparisonPercent === 100) {
    return "neutral";
  }

  return comparisonPercent > 100 ? "positive" : "negative";
};
